/**
 * Root app component for the customer-facing order SPA.
 *
 * Routes:
 *   /{slug}                  Landing / mode selector
 *   /{slug}/menu             Tier-2 menu-first IA (signed-snapshot, §5.1)
 *   /{slug}/t/{table_token}  QR mode - catalog + cart + checkout
 *   /{slug}/takeaway         Takeaway mode - catalog + cart + checkout
 *   /{slug}/delivery         Delivery (DEFERRED - static not-available screen)
 *   /{slug}/gift-cards       Token-gated gift-card purchase (?t=<widget token>)
 *   /{slug}/gift-cards/return  Post-checkout return (?st=<status token>)
 *   /o/{status_token}        Order status polling (slug-free capability)
 *   /review/{token}          Post-stay guest review form (slug-free capability)
 *   /coming-soon             Shared not-yet-available screen (§5.2)
 *   /404                     Generic not found (legacy LandingPage/QR target)
 *   *                        Catch-all / unresolved-slug → ComingSoon (§5.2)
 *
 * SL.P8 wiring is intentionally ADDITIVE: `/:slug/menu` ships the
 * snapshot-driven menu-first IA + the shared ComingSoon screen WITHOUT
 * replacing the existing LandingPage at `/:slug` (that promotion is owned by
 * the parked `hosted-order-entry-ia` W2 plan; see customer-infra-isolation
 * §5.1 "does NOT pre-empt"). MenuPage renders <ComingSoon /> inline on any
 * snapshot-fetch failure, so the no-enumeration-differential contract (§5.2
 * M1) holds on the snapshot path regardless of this route layout.
 */
import { lazy, Suspense } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Routes, Route } from 'react-router-dom'

const LandingPage = lazy(() => import('@/pages/LandingPage').then(m => ({ default: m.LandingPage })))
const MenuPage = lazy(() => import('@/pages/MenuPage').then(m => ({ default: m.MenuPage })))
const ComingSoon = lazy(() => import('@/pages/ComingSoon').then(m => ({ default: m.ComingSoon })))
const QROrderPage = lazy(() => import('@/pages/QROrderPage').then(m => ({ default: m.QROrderPage })))
const TakeawayPage = lazy(() => import('@/pages/TakeawayPage').then(m => ({ default: m.TakeawayPage })))
const DeliveryPage = lazy(() => import('@/pages/DeliveryPage').then(m => ({ default: m.DeliveryPage })))
const OrderStatusPage = lazy(() => import('@/pages/OrderStatusPage').then(m => ({ default: m.OrderStatusPage })))
const GiftCardPurchasePage = lazy(() => import('@/pages/GiftCardPurchasePage').then(m => ({ default: m.GiftCardPurchasePage })))
const GiftCardReturnPage = lazy(() => import('@/pages/GiftCardReturnPage').then(m => ({ default: m.GiftCardReturnPage })))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })))
const GuestReviewPage = lazy(() => import('@/pages/GuestReviewPage').then(m => ({ default: m.GuestReviewPage })))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 0,
    },
  },
})

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<div className="min-h-[100dvh] flex items-center justify-center"><p role="status">Loading...</p></div>}>
          <Routes>
            {/* Order status (must come before /{slug} to avoid matching "o" as a slug).
                The real slug rides in the path: the status object is still
                token-addressed (slug is public, the token is the capability), but
                the slim's status poll publishes status.query.v1 with this slug and
                the worker must resolve it to a business to refresh + ceiling. */}
            <Route path="/o/:slug/:status_token" element={<OrderStatusPage />} />

            {/* Post-stay guest review (plan guest-reviews-and-reputation-hub
                P4). Registered BEFORE the /:slug family for the same reason
                as /o/ and the gift-card routes: the single-use TOKEN is the
                capability, and there is no slug in this URL at all. Unknown,
                expired and malformed tokens all render the page's own generic
                "link no longer available" screen (no enumeration
                differential). */}
            {/* Staff preview of that same page (reviews-hub-ui P6). Declared
                BEFORE /review/:token, which would otherwise match "preview"
                as a token and answer the generic not-available screen. The
                capability is a 15-minute HMAC token minted by a
                REVIEWS_SETTINGS holder; the page fetches the preview endpoint
                and turns submit into a local state switch. */}
            <Route path="/review/preview/:ptoken" element={<GuestReviewPage />} />
            <Route path="/review/:token" element={<GuestReviewPage />} />

            {/* Shared "not yet available" screen (§5.2) - identical for
                unknown and unpublished slugs (no enumeration differential) */}
            <Route path="/coming-soon" element={<ComingSoon />} />

            {/* Not found */}
            <Route path="/404" element={<NotFoundPage />} />

            {/* Gift cards (token-gated public sales; plan
                gift-card-public-sales-and-embed P5). Registered BEFORE the
                /:slug family (mirrors the /o/ ordering note above): the
                capability is the ?t= / ?st= token, the slug is only the URL
                space. Both pages render the shared ComingSoon for any
                missing/malformed/unknown token (no enumeration
                differential). */}
            <Route path="/:slug/gift-cards" element={<GiftCardPurchasePage />} />
            <Route path="/:slug/gift-cards/return" element={<GiftCardReturnPage />} />

            {/* Slug-scoped routes */}
            <Route path="/:slug" element={<LandingPage />} />
            {/* Tier-2 menu-first IA: signed-snapshot menu-config (§5.1) */}
            <Route path="/:slug/menu" element={<MenuPage />} />
            <Route path="/:slug/t/:table_token" element={<QROrderPage />} />
            <Route path="/:slug/takeaway" element={<TakeawayPage />} />
            <Route path="/:slug/delivery" element={<DeliveryPage />} />

            {/* Catch-all / unresolved-slug fallback → shared ComingSoon
                (§5.2 - LOCKED, team-lead-confirmed 2026-05-17): render the
                shared not-yet-available screen instead of an error page so an
                unknown path/slug is indistinguishable from an unpublished one
                (no enumeration differential at the routing layer). The
                explicit `/404` route + NotFoundPage stay for the legacy
                LandingPage/QR internal `navigate('/404')` flows, which are
                intentionally untouched (parked hosted-order-entry-ia W2 plan
                owns their promotion). */}
            <Route path="*" element={<ComingSoon />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
